import { DebitCondition, MatrixData, Gender } from './types';

export class MortalityCalculator {
  private debitColumns: number[] = [];
  private ageMin: number = 50;
  private ageMax: number = 105;
  private matrices: { male: MatrixData; female: MatrixData } = { male: {}, female: {} };

  setData(debitColumns: number[], ageMin: number, ageMax: number, matrices: { male: MatrixData; female: MatrixData }) {
    this.debitColumns = debitColumns;
    this.ageMin = ageMin;
    this.ageMax = ageMax;
    this.matrices = matrices;
  }

  private pickDebitColumn(effectiveDebits: number): number {
    if (this.debitColumns.length === 0) return 0;

    let best = this.debitColumns[0];
    let dBest = Infinity;

    for (const d of this.debitColumns) {
      const diff = Math.abs(effectiveDebits - d);
      if (diff < dBest || (diff === dBest && d < best)) {
        dBest = diff;
        best = d;
      }
    }

    return best;
  }

  calculateMonths(
    gender: Gender,
    age: number,
    selectedConditions: DebitCondition[],
    manualAdjustment: number = 100
  ): { months: number | null; usedColumn: string; effectiveDebits: number; requiresReferral: boolean } {
    const requiresReferral = selectedConditions.some(condition => condition.requiresReferral);
    const totalDebits = selectedConditions.reduce((sum, condition) => {
      if (condition.requiresReferral) {
        return sum;
      }
      return sum + (condition.debits || 0);
    }, 0);
    const effectiveDebits = Math.max(0, totalDebits + manualAdjustment);
    
    const mat = this.matrices[gender];
    if (!mat || !Object.keys(mat).length) {
      return { months: null, usedColumn: '—', effectiveDebits, requiresReferral };
    }

    const a = Math.max(this.ageMin, Math.min(this.ageMax, age));
    const a0 = Math.floor(a);
    const a1 = Math.ceil(a);
    const col = this.pickDebitColumn(effectiveDebits);
    
    const clamp6 = (v: number | null): number | null => 
      (v == null || Number.isNaN(v)) ? null : Math.max(6, v);

    let months: number | null = null;
    let usedColumn: string;

    // Discrete columns; linear only on age using nearest debit column
    const colNum = col;
    const v0 = (mat[a0] || {})[colNum];
    const v1 = (mat[a1] || {})[colNum];
    const u = (a1 === a0) ? 0 : (a - a0) / (a1 - a0);
    const v = (v0 != null && v1 != null) ? (1 - u) * v0 + u * v1 : (v0 != null ? v0 : v1);

    months = clamp6(v);
    usedColumn = `${colNum}`;

    return { months, usedColumn, effectiveDebits, requiresReferral };
  }
}
